'use client';

import { useState, useEffect, useMemo, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { cn, Skeleton } from '@sharpr/ui';
import { MarketingFeed } from '@/components/client/landing/MarketingFeed';
import { NbaResultsSection } from '@/components/client/landing/NbaResultsSection';
import { ResultsProofPill } from '@/components/client/landing/ResultsProofPill';
import {
  HeroAnchorLinks,
  OddsScreenshotFrame,
  SharprWordmark,
} from '@/components/client/landing/shared';
import { getSportName } from '@/utils/sportName';
import {
  AlertCircle,
  Check,
  Zap,
  Loader2,
  X,
  Tag,
  ArrowRight,
  ExternalLink,
} from 'lucide-react';
import { SPORT_ICONS } from '@/utils/sportIcons';
import { formatPrice } from '@/utils/formatPrice';
import { buildSignupSummary, offerTrialForCart, openerWait } from '@/services/billing/buildSignupSummary';
import { disclosedFirstChargeCents } from '@/lib/billing/trialConsent';
import { discountedCents } from '@/utils/discountedCents';
import { useSession } from '@/hooks/useSession';
import { accessStateQuery, useAccessState, useAwaitAccess } from '@/hooks/useAccessState';
import { useSportSeasons } from '@/hooks/useSportSeasons';
import {
  isWithinSeasonCtaWindow,
  pendingCoverageStartMs,
} from '@/services/billing/seasonClamp';
import { formatBillingDate } from '@/utils/formatBillingDate';
import { getStoredReferralCode } from '@/utils/referralStorage';
import { useReferralCode } from '@/hooks/useReferralCode';
import { AddSportDialog } from '@/components/client/AddSportDialog';
import type { ProductWithPricing, Sport, UserAccess } from '@sharpr/types';
import Link from 'next/link';

interface PricingPageProps {
  checkoutCanceled?: boolean;
  referralCode?: string;
}

/** Referral-code affordance: applied chip / inline input / "Add code" button. */
function ReferralCodeControl({
  appliedCode,
  appliedDiscountLabel,
  showInput,
  input,
  validating,
  onInputChange,
  onApply,
  onOpen,
  onCancel,
}: {
  appliedCode: string | null;
  appliedDiscountLabel: string | null;
  showInput: boolean;
  input: string;
  validating: boolean;
  onInputChange: (value: string) => void;
  onApply: () => void;
  onOpen: () => void;
  onCancel: () => void;
}) {
  if (appliedCode) {
    return (
      <span className="inline-flex items-center gap-1.5 px-2 py-1 rounded-md border border-emerald-500/20 bg-emerald-500/5 text-[11px] text-muted-foreground dark:text-text-tertiary">
        <Tag className="w-2.5 h-2.5 shrink-0 text-emerald-600 dark:text-emerald-400" />
        <span className="inline-flex flex-col items-start sm:flex-row sm:items-center sm:gap-1.5 leading-tight">
          <span className="font-medium text-fg dark:text-text-primary">{appliedCode}</span>
          {appliedDiscountLabel && (
            <span className="text-emerald-700 dark:text-emerald-400">
              <span className="hidden sm:inline">· </span>{appliedDiscountLabel}
            </span>
          )}
        </span>
      </span>
    );
  }
  if (showInput) {
    return (
      <span className="inline-flex items-center gap-1.5 text-[11px] text-muted-foreground dark:text-text-tertiary">
        <input
          type="text"
          value={input}
          onChange={(e) => onInputChange(e.target.value)}
          onKeyDown={(e) => { if (e.key === 'Enter' && input.trim()) onApply(); }}
          placeholder="code"
          className="w-24 px-2 py-1 rounded-md font-mono text-[11px] border border-border dark:border-border-primary bg-white dark:bg-bg-tertiary text-fg dark:text-text-primary placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-blue-500/50"
          disabled={validating}
          autoFocus
        />
        <button
          onClick={() => { if (input.trim()) onApply(); }}
          disabled={!input.trim() || validating}
          className="font-medium text-blue-600 dark:text-blue-400 hover:underline disabled:opacity-50"
        >
          {validating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Apply'}
        </button>
        <button
          onClick={onCancel}
          className="text-muted-foreground hover:text-fg dark:hover:text-text-primary"
          aria-label="Cancel"
        >
          <X className="w-3 h-3" />
        </button>
      </span>
    );
  }
  return (
    <button
      onClick={onOpen}
      className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border dark:border-border-primary text-[11px] font-medium text-muted-foreground dark:text-text-tertiary hover:border-fg/30 hover:text-fg dark:hover:text-text-primary transition-colors"
    >
      <Tag className="w-3 h-3" />
      Add code
    </button>
  );
}

/** Price with an optional applied discount: strikethrough base + discounted
 *  figure, or the plain base when no discount is active. */
function PriceWithDiscount({
  baseCents,
  suffix,
  discount,
}: {
  baseCents: number;
  suffix: string;
  discount: { percentOff?: number; amountOffCents?: number } | null;
}) {
  if (!discount) return <>{`${formatPrice(baseCents)}${suffix}`}</>;
  return (
    <>
      <span className="line-through opacity-60 mr-1">{formatPrice(baseCents)}</span>
      <span className="text-fg dark:text-text-primary">
        {formatPrice(discountedCents(baseCents, discount))}
      </span>
      {suffix}
    </>
  );
}

// Sport icon chip styling. Neutral palette — the icon shape (basketball/baseball)
// carries the differentiation, not arbitrary color, so we don't introduce hues
// that fight the brand blue + celebratory emerald already on the page.
const SPORT_CHIP = {
  bg: 'bg-fg/[0.04] dark:bg-bg-tertiary/60',
  ring: 'ring-1 ring-inset ring-border/50 dark:ring-border-primary/40',
  icon: 'text-fg/75 dark:text-text-primary/85',
};

// Shared access-record predicates, hoisted to avoid re-spelling the filter logic.
function isOnTrial(records: UserAccess[]): boolean {
  return records.some((r) => r.is_trial);
}
function hasActiveMonthlySub(records: UserAccess[]): boolean {
  return records.some(
    (r) => r.type === 'monthly' && r.status === 'active' && r.stripe_object_type === 'subscription'
  );
}
function ownedMonthlySportSet(records: UserAccess[]): Set<Sport> {
  return new Set(records.filter((r) => r.type === 'monthly').map((r) => r.sport));
}

export function PricingPage({
  checkoutCanceled,
  referralCode: referralCodeProp,
}: PricingPageProps) {
  const { user, loading: userLoading } = useSession();
  const { accessState, loading: accessStateLoading, hasAccess } = useAccessState();
  // Off-season sports can't be signed up for (server returns season_ended), so they're
  // shown disabled with an "Off-season" badge instead of a selectable price. Public hook
  // (this page is anon) + the existing CTA-window helper, which fails open on missing data.
  const seasons = useSportSeasons();
  const isSportOffSeason = (sport: Sport) =>
    !isWithinSeasonCtaWindow(seasons.find((s) => s.sport === sport));
  const queryClient = useQueryClient();

  const [products, setProducts] = useState<ProductWithPricing[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [checkoutLoading, setCheckoutLoading] = useState<string | null>(null);
  const [showBanner, setShowBanner] = useState(checkoutCanceled);
  const [addDialogSport, setAddDialogSport] = useState<Sport | null>(null);
  // A PAID add's user_access row lands async (client PI-confirm + webhook reconcile), so a
  // single refetch can beat the row: await it until the sport shows active.
  const { awaitChange: awaitAddedSport } = useAwaitAccess<Sport>(
    (state, sport) => state.accessRecords.some((r) => r.sport === sport && r.status === 'active'),
  );

  // Referral/promo code state — revalidates ?ref= / stored code on mount (stale self-clears).
  const {
    appliedReferralCode,
    appliedDiscountLabel,
    appliedDiscount,
    referralInput,
    setReferralInput,
    referralError,
    setReferralError,
    referralValidating,
    showReferralInput,
    setShowReferralInput,
    validateAndApplyReferralCode,
  } = useReferralCode(referralCodeProp);

  const [portalLoading, setPortalLoading] = useState(false);

  // Multi-select: which single-sport monthly products the user wants to bundle into one checkout.
  const [selectedForMulti, setSelectedForMulti] = useState<Set<string>>(new Set());

  const toggleMultiSelect = (productId: string) => {
    setSelectedForMulti((prev) => {
      const next = new Set(prev);
      if (next.has(productId)) next.delete(productId);
      else next.add(productId);
      return next;
    });
  };

  const addOnSport: Sport | null = useMemo(() => {
    if (!user || accessState.billingHealth !== 'ok') return null;
    if (selectedForMulti.size !== 1) return null;
    const productId = Array.from(selectedForMulti)[0];
    const product = products.find((p) => p.id === productId);
    if (!product || product.sport == null) return null;
    const sport = product.sport;
    if (ownedMonthlySportSet(accessState.accessRecords).has(sport)) return null;
    if (!hasActiveMonthlySub(accessState.accessRecords)) return null;
    return sport;
  }, [user, accessState, selectedForMulti, products]);

  const addOnIsTrial = useMemo(
    () => addOnSport != null && isOnTrial(accessState.accessRecords),
    [addOnSport, accessState.accessRecords]
  );

  const handleAddOnSubscribe = (sport: Sport) => {
    setError(null);
    setAddDialogSport(sport);
  };

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    try {
      const response = await fetch('/api/billing/create-portal', { method: 'POST' });
      if (!response.ok) {
        const json = await response.json();
        throw new Error(json.error || `HTTP ${response.status}`);
      }
      const json = await response.json();
      if (json.data?.portal_url) {
        window.location.href = json.data.portal_url;
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open billing portal');
    } finally {
      setPortalLoading(false);
    }
  };

  useEffect(() => {
    async function fetchProducts() {
      try {
        const response = await fetch('/api/billing/products');
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        const json = await response.json();
        setProducts(json.data?.products || []);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load products');
      } finally {
        setLoading(false);
      }
    }
    fetchProducts();
  }, []);

  // Default selection: every single-sport monthly product that the user can
  // actually buy (skip already-owned and trial-disabled). Runs once after both
  // products and subscription have loaded; subsequent user toggles win.
  const defaultedSelectionRef = useRef(false);
  useEffect(() => {
    if (defaultedSelectionRef.current) return;
    if (loading || accessStateLoading) return;
    const onTrial = isOnTrial(accessState.accessRecords);
    const owned = new Set(
      accessState.accessRecords
        .filter((r) => r.type === 'monthly' && r.status === 'active')
        .map((r) => r.sport)
    );
    const defaultIds = products
      .filter((p) => p.sport != null && p.monthly_price_cents > 0)
      .filter((p) => !onTrial && !owned.has(p.sport as Sport))
      // Don't default-select an off-season sport (it can't be purchased). Inlined (not the
      // isSportOffSeason helper) to keep the dep array stable.
      .filter((p) => isWithinSeasonCtaWindow(seasons.find((s) => s.sport === (p.sport as Sport))))
      .map((p) => p.id);
    if (defaultIds.length > 0) {
      setSelectedForMulti(new Set(defaultIds));
    }
    defaultedSelectionRef.current = true;
  }, [loading, accessStateLoading, products, accessState, seasons]);

  const handleCheckout = (productIds: string[]) => {
    if (!user) {
      const currentReferralCode = appliedReferralCode ?? getStoredReferralCode();
      // pending_checkout.productIds uses array form for both single-sport and multi-sport flows.
      localStorage.setItem('pending_checkout', JSON.stringify({
        productIds,
        ...(currentReferralCode ? { referralCode: currentReferralCode } : {}),
      }));
      window.location.href = '/signup';
      return;
    }

    if (productIds.length === 1) {
      const requested = products.find((p) => p.id === productIds[0]);
      // Add-on flow tolerates past_due (recovery in-portal), unlike hasActiveMonthlySub.
      const hasActiveMonthly = accessState.accessRecords.some(
        (r) =>
          r.type === 'monthly' &&
          (r.status === 'active' || r.status === 'past_due') &&
          r.stripe_object_type === 'subscription'
      );
      const isSingleSportProduct = requested?.sport != null;
      const isUnownedSport =
        isSingleSportProduct &&
        !ownedMonthlySportSet(accessState.accessRecords).has(requested!.sport as Sport);
      if (hasActiveMonthly && isUnownedSport) {
        if (accessState.billingHealth === 'failing') {
          window.location.href = '/settings';
          return;
        }
        handleAddOnSubscribe(requested!.sport as Sport);
        return;
      }
    }

    // Authed user, no active sub (or canceled / multi-sport selection): same
    // handoff as unauthed — write pending_checkout, navigate to /checkout/pending
    // which mounts the embedded form. Server-side validation errors surface there.
    const currentReferralCode = appliedReferralCode ?? getStoredReferralCode();
    setCheckoutLoading(productIds.length === 1 ? productIds[0] : 'multi');
    setError(null);
    localStorage.setItem('pending_checkout', JSON.stringify({
      productIds,
      ...(currentReferralCode ? { referralCode: currentReferralCode } : {}),
    }));
    window.location.href = '/checkout/pending';
  };

  // Loading skeleton — mirrors the hero + pricing-card two-column layout.
  // min-h-screen: the loaded page renders several sections below the grid, so
  // anything server-rendered after this island (the support/FAQ footer) sits
  // just below the skeleton at first paint and then travels thousands of px
  // when products load — measured as the dominant anon CLS on /pricing
  // (0.112 of the 0.255 desktop p75). Filling the viewport pushes the footer
  // below the fold, where its movement doesn't count toward CLS.
  if (loading) {
    return (
      <div className="max-w-7xl mx-auto min-h-screen">
      <div className="mb-8 grid grid-cols-1 lg:grid-cols-[5fr_4fr] gap-6 lg:gap-10 items-start pt-2">
        {/* Left: hero copy + screenshot placeholder */}
        <div className="contents lg:flex lg:flex-col lg:gap-6">
          <div className="w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0 space-y-4">
            <Skeleton className="h-10 sm:h-12 w-4/5 dark:bg-bg-tertiary" />
            <div className="space-y-2 max-w-lg">
              <Skeleton className="h-4 w-full dark:bg-bg-tertiary" />
              <Skeleton className="h-4 w-5/6 dark:bg-bg-tertiary" />
            </div>
          </div>
          <div className="hidden lg:block w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0">
            <Skeleton className="h-[360px] rounded-lg border border-border dark:border-border-primary dark:bg-bg-tertiary" />
          </div>
        </div>
        {/* Right: pricing card with header + product rows + order summary */}
        <div className="w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0 flex flex-col overflow-hidden rounded-xl border border-border dark:border-border-primary bg-white dark:bg-bg-secondary shadow-card">
          <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-border/40 dark:border-border-primary/40 flex items-center gap-3">
            <Skeleton className="h-9 w-9 flex-shrink-0 rounded-lg dark:bg-bg-tertiary" />
            <div className="min-w-0 flex-1 space-y-2">
              <Skeleton className="h-4 w-32 dark:bg-bg-tertiary" />
              <Skeleton className="h-3 w-48 dark:bg-bg-tertiary" />
            </div>
          </div>
          <div className="divide-y divide-border/40 dark:divide-border-primary/40">
            {[1, 2].map((i) => (
              <div key={i} className="flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5">
                <div className="h-5 w-5 flex-shrink-0 rounded border-2 border-border dark:border-border-primary" />
                <Skeleton className="h-9 w-9 flex-shrink-0 rounded-lg dark:bg-bg-tertiary" />
                <Skeleton className="flex-1 h-5 max-w-[8rem] dark:bg-bg-tertiary" />
                <Skeleton className="h-6 w-16 dark:bg-bg-tertiary" />
              </div>
            ))}
          </div>
          <div className="border-t-2 border-border/60 dark:border-border-primary/60 bg-muted/30 dark:bg-bg-overlay/40 px-4 sm:px-6 py-5 space-y-3">
            <Skeleton className="h-5 w-full dark:bg-bg-tertiary" />
            <Skeleton className="h-10 w-full dark:bg-bg-tertiary" />
          </div>
        </div>
      </div>
      </div>
    );
  }

  // Error state
  if (error && !products.length) {
    return (
      <div className="max-w-md mx-auto rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50 dark:bg-red-950/20 p-6">
        <div className="flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 mt-0.5 flex-shrink-0" />
          <div className="flex-1">
            <p className="text-sm font-medium text-fg dark:text-text-primary">Failed to load pricing</p>
            <p className="text-sm text-muted-foreground dark:text-text-secondary mt-1">{error}</p>
            <button
              onClick={() => window.location.reload()}
              className="mt-3 inline-flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-fg dark:text-text-primary border border-border dark:border-border-primary rounded-md hover:bg-muted/50 dark:hover:bg-bg-overlay transition-colors"
            >
              Retry
            </button>
          </div>
        </div>
      </div>
    );
  }

  const isOnActiveTrial = isOnTrial(accessState.accessRecords);
  // Stable across selection toggles (drives the hero headline).
  const trialEligible = !hasAccess && !accessState.trialUsed;

  // Single-sport monthly products only — bundle/multi-sport products are derived
  // automatically when a user picks 2+ sports (sum-of-parts).
  const singleSportProducts = products.filter(
    (p) => p.sport != null && p.monthly_price_cents > 0
  );
  const activeSports = new Set(
    accessState.accessRecords.filter((r) => r.status === 'active').map((r) => r.sport)
  );
  const ownedMonthlySports = new Set(
    accessState.accessRecords
      .filter((r) => r.type === 'monthly' && r.status === 'active')
      .map((r) => r.sport)
  );

  // Exclude off-season sports defensively: even if one lingers in selectedForMulti (selected
  // before seasons loaded), it must never enter the cart/CTA — checkout would 409 season_ended.
  const selectedProducts = products.filter(
    (p) => selectedForMulti.has(p.id) && !isSportOffSeason(p.sport as Sport),
  );
  const selectedTotalCents = selectedProducts.reduce(
    (sum, p) => sum + p.monthly_price_cents,
    0
  );
  // Season-tail offer rendered through the SAME engine checkout-setup uses
  // (suppressTrialForTail + buildSignupSummary + disclosedFirstChargeCents) —
  // pricing cannot drift from what checkout will actually charge.
  const selectedTailProducts = selectedProducts.filter((p) => p.season_tail);
  const selectedHasTail = selectedTailProducts.length > 0;
  const selectedSports = selectedProducts
    .map((p) => p.sport)
    .filter((s): s is Sport => s != null);
  const tailSports = new Set(
    selectedTailProducts.map((p) => p.sport).filter((s): s is Sport => s != null)
  );
  const showTrialCTA = offerTrialForCart(trialEligible, selectedSports, tailSports);
  // After the trial, only NON-tail sports charge (tail sports charge next
  // season). Equals selectedTotalCents for a pure-trial cart.
  const afterTrialMonthlyCents = selectedProducts
    .filter((p) => !p.season_tail)
    .reduce((sum, p) => sum + p.monthly_price_cents, 0);
  const offerNowMs = Date.now();
  const offer = selectedProducts.length > 0
    ? buildSignupSummary({
        products: selectedProducts,
        trialOffered: showTrialCTA,
        trialDays: Number(process.env.NEXT_PUBLIC_TRIAL_DAYS || 5),
        nowMs: offerNowMs,
        ...(tailSports.size > 0 ? { tailSports } : {}),
        // Same pre-sale opener checkout-setup computes.
        coverageStartMs:
          pendingCoverageStartMs(selectedSports, seasons, offerNowMs) ?? undefined,
      })
    : null;
  const dueTodayCents = offer ? disclosedFirstChargeCents(offer) : 0;
  const selectedSportLabel = selectedProducts
    .map((p) => (p.sport != null ? getSportName(p.sport) : null))
    .filter((s): s is string => s != null)
    .join(' + ');

  // Primary CTA label shifts with context: anonymous trial, signed-in first
  // buy, or add-on to an existing sub.
  const hasActiveMonthly = hasActiveMonthlySub(accessState.accessRecords);
  const primaryCtaLabel = (() => {
    if (selectedForMulti.size === 0) return 'Select a sport to continue';
    if (showTrialCTA) return 'Start free trial';
    if (hasActiveMonthly) {
      if (selectedForMulti.size === 1) return `Add ${selectedSportLabel} to subscription`;
      return `Add ${selectedForMulti.size} sports to subscription`;
    }
    return 'Subscribe';
  })();

  // Order-summary "due today" line. Add-on omits the figure — proration is
  // server-side, so a client estimate would risk drift from the real charge.
  const isAddOn = hasActiveMonthly && !!addOnSport;
  // Pre-sale: the cart waits for the opener (trial starts there, or billing does). Same
  // builder checkout-setup / addSportPlan use. An add-on into a trialing cohort inherits
  // that trial instead, so it does not wait.
  const wait =
    isAddOn && addOnIsTrial
      ? null
      : openerWait(showTrialCTA, offer?.coverage_start != null ? offer.coverage_start * 1000 : null);
  const presaleDeferred = wait?.kind === 'billing';
  const openerDate = wait ? formatBillingDate(new Date(wait.startMs), 'short') : null;
  const todayLabel = (() => {
    if (presaleDeferred) return 'Due today';
    if (isAddOn) return 'Charged today, prorated';
    if (showTrialCTA || selectedHasTail) return 'Due today';
    return 'Due monthly';
  })();
  const todayValue = (() => {
    if (showTrialCTA || presaleDeferred) return 'Free';
    if (isAddOn) return addOnIsTrial ? 'Free' : '—';
    if (selectedHasTail) return dueTodayCents === 0 ? 'Free' : formatPrice(dueTodayCents);
    return formatPrice(selectedTotalCents);
  })();
  const isFree = todayValue === 'Free';
  const trialDays = process.env.NEXT_PUBLIC_TRIAL_DAYS || 5;
  const showThenRow =
    showTrialCTA || presaleDeferred || selectedHasTail || (isAddOn && addOnIsTrial);

  return (
    <>
      {/* Error toast — page-fixed, lives outside the column flow */}
      {error && products.length > 0 && (
        <div className="fixed top-4 right-4 z-50 max-w-md">
          <div className="rounded-lg border border-red-200 dark:border-red-500/20 bg-red-50/95 dark:bg-red-950/90 backdrop-blur-sm p-4 shadow-lg">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-4 h-4 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <p className="text-sm font-medium text-red-600 dark:text-red-400 flex-1">{error}</p>
              <button onClick={() => setError(null)} className="text-red-600/60 dark:text-red-400/60 hover:text-red-600 dark:hover:text-red-400 transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="max-w-7xl mx-auto">
      {/* Status banners */}
      {showBanner && (
        <div className="mb-6 px-4 py-3 rounded-lg border border-blue-500/20 bg-blue-500/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-fg dark:text-text-primary">Checkout canceled. You can try again when ready.</span>
          </div>
          <button onClick={() => setShowBanner(false)} className="text-muted-foreground hover:text-fg dark:hover:text-text-primary transition-colors">
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {hasAccess && !accessState.isAdmin && isOnActiveTrial && (
        <div className="mb-6 px-4 py-3 rounded-lg border border-blue-500/20 bg-blue-500/5 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Zap className="w-4 h-4 text-blue-600 dark:text-blue-400" />
            <span className="text-sm text-fg dark:text-text-primary">
              You&apos;re on a free trial — full access to all sports.
            </span>
          </div>
          <Link href="/settings" className="text-xs font-medium text-blue-600 dark:text-blue-400 hover:underline flex-shrink-0">
            Manage
          </Link>
        </div>
      )}

      {/* Above-the-fold composition. The LEFT wrapper uses display:contents on
          mobile so hero + screenshot flatten into the grid (allowing mobile
          reorder: hero → pricing card → screenshot via order-last on screenshot).
          At lg+, the wrapper becomes flex flex-col and hero+screenshot stack
          at natural content height. items-start on mobile (irrelevant — each
          child is its own row), lg:items-end so the pricing card's bottom
          edge aligns with the screenshot's bottom edge on desktop. */}
      <div className="mb-4 sm:mb-6 grid grid-cols-1 lg:grid-cols-[5fr_4fr] gap-6 lg:gap-10 items-start lg:items-end">

        {/* Left side: hero + screenshot. Flattens on mobile (contents), stacks
            as flex column on desktop at natural content height. */}
        <div className="contents lg:flex lg:flex-col lg:gap-6">
          {/* Hero — mirrors the landing hero, sized for this layout. */}
          <div className="w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0">
            <h1 className="text-center sm:text-left text-2xl sm:text-4xl lg:text-[3.25rem] font-bold tracking-tight leading-tight text-fg">
              {trialEligible ? (
                <>
                  Find the{' '}
                  <SharprWordmark className="h-7 sm:h-9 lg:h-[4.5rem] align-[-0.27em] sm:align-[-0.24em] lg:align-[-0.37em]" />{' '}
                  Edge
                </>
              ) : hasAccess ? (
                'Add sports'
              ) : (
                'Start your subscription'
              )}
            </h1>
            <p className="mt-1 sm:mt-3 max-w-md mx-auto sm:mx-0 text-center sm:text-left text-sm sm:text-base font-medium text-balance text-fg/65 md:text-fg/70 leading-snug sm:leading-relaxed">
              <HeroAnchorLinks />
            </p>
            {/* Desktop only — on phones the results section sits right below the card. */}
            <div className="mt-3 hidden sm:flex justify-start">
              <ResultsProofPill href="#results" />
            </div>
          </div>

          {/* Screenshot — desktop only. Capped height shows the top rows of
              the odds dashboard; gradient fade hides the cropped bottom. */}
          <div className="hidden lg:block w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0 relative">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute inset-x-8 -bottom-4 h-12 rounded-[50%] bg-blue-500/20 blur-2xl"
            />
            <OddsScreenshotFrame fade frameClassName="relative" />
          </div>
        </div>

        {/* Pricing card — appears between hero and screenshot on mobile (DOM order
            after the contents wrapper, but before the order-last screenshot inside it).
            On desktop it sits in column 2. */}
        <div className="w-full max-w-2xl mx-auto lg:max-w-none lg:mx-0 flex flex-col overflow-hidden rounded-xl border border-border dark:border-border-primary bg-white dark:bg-bg-secondary shadow-card">

          {/* Card header — Sharpr icon mark anchors the brand inside the card,
              title to the right. */}
          <div className="px-4 sm:px-6 pt-5 pb-4 border-b border-border/40 dark:border-border-primary/40 flex items-center gap-3">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img
              src="/sharpr-icon.png"
              alt=""
              aria-hidden="true"
              className="h-9 w-9 flex-shrink-0 rounded-lg"
            />
            <div className="min-w-0">
              <h3 className="text-base font-semibold tracking-tight text-fg dark:text-text-primary">
                Pick your sports
              </h3>
            </div>
          </div>

          {/* Feed rows */}
          <div className="divide-y divide-border/40 dark:divide-border-primary/40">
            {singleSportProducts.map((product) => {
              const sport = product.sport as Sport;
              const SportIcon = SPORT_ICONS[sport];
              const hasMonthly = ownedMonthlySports.has(sport);
              const isTrialing = isOnActiveTrial && activeSports.has(sport);
              const offSeason = isSportOffSeason(sport);
              // Off-season sports can't be purchased (server returns season_ended) → not
              // selectable, never checked.
              const disabled = hasMonthly || isOnActiveTrial || offSeason;
              const isChecked = selectedForMulti.has(product.id) && !offSeason;

              return (
                <div
                  key={product.id}
                  className={cn(
                    'group/row relative flex items-center gap-3 sm:gap-5 px-4 sm:px-6 py-4 sm:py-5 transition-all duration-200',
                    !disabled && 'cursor-pointer hover:bg-muted/30 dark:hover:bg-bg-overlay/40',
                    isChecked && 'bg-blue-500/[0.05] dark:bg-blue-500/[0.07] shadow-[inset_0_1px_0_0_rgba(59,130,246,0.05)]'
                  )}
                  role={!disabled ? 'button' : undefined}
                  tabIndex={!disabled ? 0 : undefined}
                  aria-pressed={!disabled ? isChecked : undefined}
                  onClick={() => !disabled && toggleMultiSelect(product.id)}
                  onKeyDown={(e) => {
                    if (!disabled && (e.key === 'Enter' || e.key === ' ')) {
                      e.preventDefault();
                      toggleMultiSelect(product.id);
                    }
                  }}
                >
                  {isChecked && (
                    <span
                      aria-hidden="true"
                      className="absolute left-0 top-0 h-full w-[3px] bg-gradient-to-b from-blue-400 via-blue-500 to-blue-600 shadow-[0_0_12px_rgba(59,130,246,0.5)]"
                    />
                  )}
                  <div
                    aria-hidden="true"
                    className={cn(
                      'flex h-5 w-5 flex-shrink-0 items-center justify-center rounded border-2 transition-all duration-200',
                      disabled
                        ? 'border-border/50 dark:border-border-primary/50 bg-muted/40 dark:bg-bg-overlay/40'
                        : isChecked
                          ? 'border-blue-600 bg-blue-600 shadow-[0_0_0_3px_rgba(59,130,246,0.15)]'
                          : 'border-border dark:border-border-primary group-hover/row:border-blue-500/60'
                    )}
                  >
                    {(isChecked || hasMonthly || isTrialing) && (
                      <Check className="h-3 w-3 text-white" strokeWidth={3} />
                    )}
                  </div>

                  <div className="flex items-center gap-3 min-w-0 flex-1">
                    <div
                      aria-hidden="true"
                      className={cn(
                        'flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-lg transition-transform duration-200',
                        SPORT_CHIP.bg,
                        SPORT_CHIP.ring,
                        !disabled && 'group-hover/row:scale-105'
                      )}
                    >
                      <SportIcon className={cn('h-5 w-5', SPORT_CHIP.icon)} />
                    </div>
                    <div className="min-w-0">
                      <div className="text-base font-semibold text-fg dark:text-text-primary tracking-tight">
                        {getSportName(sport)}
                      </div>
                    </div>
                  </div>

                  <div className="flex-shrink-0 text-right">
                    {hasMonthly ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-emerald-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-emerald-700 dark:text-emerald-400">
                        Active
                      </span>
                    ) : isTrialing ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-blue-500/10 px-2 py-0.5 text-[10px] uppercase tracking-wider text-blue-700 dark:text-blue-400">
                        Trial
                      </span>
                    ) : offSeason ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="inline-flex items-center gap-1 rounded-full bg-muted/60 dark:bg-bg-overlay/60 px-2 py-0.5 text-[10px] uppercase tracking-wider text-muted-foreground dark:text-text-tertiary">
                          Off-season
                        </span>
                        <span className="text-[11px] text-muted-foreground dark:text-text-tertiary whitespace-nowrap">
                          {formatPrice(product.monthly_price_cents)}/mo when the season starts
                        </span>
                      </div>
                    ) : product.season_tail ? (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xl font-semibold text-emerald-600 dark:text-emerald-400 whitespace-nowrap">
                          Free rest of season
                        </span>
                        <span className="text-[11px] text-muted-foreground dark:text-text-tertiary whitespace-nowrap">
                          then {formatPrice(product.monthly_price_cents)}/mo next season
                        </span>
                      </div>
                    ) : (
                      <div className="flex flex-col items-end gap-0.5">
                        <span className="text-xl font-semibold text-fg dark:text-text-primary tabular-nums">
                          {formatPrice(product.monthly_price_cents)}
                        </span>
                        <span className="text-[11px] text-muted-foreground dark:text-text-tertiary">
                          {(() => {
                            const startMs = pendingCoverageStartMs([sport], seasons, Date.now());
                            if (startMs == null) return 'per month';
                            const date = formatBillingDate(new Date(startMs), 'short');
                            return trialEligible
                              ? `per month · trial starts ${date}`
                              : `per month · billing starts ${date}`;
                          })()}
                        </span>
                      </div>
                    )}
                  </div>
                </div>
              );
            })}

          </div>

          {/* Order summary — Stripe checkout pattern. Header + line-item
              breakdown + chip footer + single-line CTA. */}
          <div className="relative border-t-2 border-border/60 dark:border-border-primary/60 bg-muted/30 dark:bg-bg-overlay/40">
            <div
              aria-hidden="true"
              className="pointer-events-none absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-white/10 dark:via-white/[0.06] to-transparent"
            />

            {/* Section header — mirrors the top card header structure for
                visual coherence (Pick your sports / Order summary). The code
                affordance lives here instead of in a footer row: applied =
                emerald chip, entering = inline input + Apply, idle = button. */}
            <div className="px-4 sm:px-6 pt-5 pb-3 flex items-center justify-between gap-3">
              <h3 className="text-base font-semibold tracking-tight text-fg dark:text-text-primary">
                Order summary
              </h3>
              <div className="flex items-center gap-2">
              {hasActiveMonthly && !isOnActiveTrial && (
                <button
                  onClick={handleManageSubscription}
                  disabled={portalLoading}
                  className="inline-flex items-center gap-1 px-2.5 py-1 rounded-md border border-border dark:border-border-primary text-[11px] font-medium text-muted-foreground dark:text-text-tertiary hover:border-fg/30 hover:text-fg dark:hover:text-text-primary transition-colors disabled:opacity-50"
                >
                  {portalLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <ExternalLink className="w-3 h-3" />}
                  Manage
                </button>
              )}
              <ReferralCodeControl
                appliedCode={appliedReferralCode}
                appliedDiscountLabel={appliedDiscountLabel}
                showInput={showReferralInput}
                input={referralInput}
                validating={referralValidating}
                onInputChange={(value) => { setReferralInput(value); setReferralError(null); }}
                onApply={() => validateAndApplyReferralCode(referralInput.trim())}
                onOpen={() => setShowReferralInput(true)}
                onCancel={() => { setShowReferralInput(false); setReferralInput(''); setReferralError(null); }}
              />
              </div>
            </div>

            {selectedProducts.length > 0 ? (
              <div className="px-4 sm:px-6 pb-4">
                {/* Primary line — due today */}
                <div className="flex items-baseline justify-between gap-3">
                  <span className="text-sm font-medium text-fg dark:text-text-primary">
                    {todayLabel}
                  </span>
                  <span
                    className={cn(
                      'text-2xl sm:text-3xl font-semibold tabular-nums leading-none',
                      isFree
                        ? 'bg-gradient-to-br from-emerald-500 to-emerald-400 bg-clip-text text-transparent dark:from-emerald-300 dark:to-emerald-400'
                        : 'text-fg dark:text-text-primary'
                    )}
                  >
                    {todayValue}
                    {!isFree && todayValue !== '—' && (
                      <span className="ml-1 text-sm font-normal text-muted-foreground dark:text-text-tertiary">
                        + tax
                      </span>
                    )}
                  </span>
                </div>

                {/* Secondary line — post-trial commitment: the recurring monthly rate. */}
                {showThenRow && (
                  <div className="mt-5 pt-4 border-t border-border/40 dark:border-border-primary/40 flex items-baseline justify-between gap-3">
                    <span className="text-xs text-muted-foreground dark:text-text-tertiary">
                      {wait?.kind === 'trial'
                        ? `After ${trialDays}-day trial from ${openerDate}`
                        : wait?.kind === 'billing'
                          ? `From opening night, ${openerDate}`
                          : showTrialCTA
                            ? `After ${trialDays}-day trial`
                            : 'Monthly rate'}
                    </span>
                    <span className="text-xs font-medium text-muted-foreground dark:text-text-tertiary tabular-nums">
                      <PriceWithDiscount
                        baseCents={afterTrialMonthlyCents}
                        suffix={'/mo + tax'}
                        discount={appliedDiscount}
                      />
                    </span>
                  </div>
                )}

                {referralError && (
                  <p className="mt-3 text-[11px] text-red-600 dark:text-red-400">{referralError}</p>
                )}
              </div>
            ) : (
              <div className="px-4 sm:px-6 pb-4 text-sm text-muted-foreground dark:text-text-tertiary">
                Select a sport above to see your total.
              </div>
            )}

            <div className="px-4 sm:px-6 pb-5 sm:pb-6">
              <button
                onClick={() => handleCheckout(Array.from(selectedForMulti))}
                disabled={
                  selectedForMulti.size === 0 ||
                  checkoutLoading !== null ||
                  addDialogSport !== null ||
                  userLoading ||
                  accessStateLoading ||
                  isOnActiveTrial
                }
                className={cn(
                  'group/cta relative w-full inline-flex items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold tracking-tight transition-all duration-200',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-blue-500/60 focus-visible:ring-offset-2 focus-visible:ring-offset-bg-secondary',
                  selectedForMulti.size === 0
                    ? 'border border-border dark:border-border-primary bg-transparent text-muted-foreground dark:text-text-tertiary cursor-not-allowed'
                    : 'bg-gradient-to-b from-blue-500 to-blue-600 text-white hover:from-blue-500 hover:to-blue-700 active:scale-[0.99] disabled:opacity-50 disabled:cursor-not-allowed disabled:active:scale-100 shadow-[0_1px_2px_0_rgba(0,0,0,0.2),inset_0_1px_0_0_rgba(255,255,255,0.18)] hover:shadow-[0_2px_4px_0_rgba(0,0,0,0.25),inset_0_1px_0_0_rgba(255,255,255,0.22)]'
                )}
              >
                {checkoutLoading !== null ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    {primaryCtaLabel}
                    {selectedForMulti.size > 0 && (
                      <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover/cta:translate-x-0.5" />
                    )}
                  </>
                )}
              </button>
            </div>
          </div>

        </div>
      </div>

      </div>

      {/* Below the fold — tracked results, then the shared landing product story. */}
      <NbaResultsSection compact />
      <MarketingFeed />

      <div className="max-w-7xl mx-auto">
        <div className="text-center text-[10px] uppercase tracking-[0.22em] text-muted-foreground dark:text-text-tertiary">
          {!user && (
            <p>
              <span className="opacity-60">Already a customer?</span>{' '}
              <Link href={`/login?redirect=${encodeURIComponent('/pricing')}`} className="text-blue-600 dark:text-blue-400 hover:underline">
                Sign in
              </Link>
            </p>
          )}
        </div>
      </div>

      {addDialogSport && (
        <AddSportDialog
          sport={addDialogSport}
          onClose={() => setAddDialogSport(null)}
          onAdded={() => {
            const s = addDialogSport;
            setAddDialogSport(null);
            queryClient.invalidateQueries({ queryKey: accessStateQuery(user?.id).queryKey });
            if (s) awaitAddedSport(s);
          }}
        />
      )}
    </>
  );
}
